import {
  Component,
  OnInit,
  OnDestroy,
  Optional,
  Inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ElementRef,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GridBuilderService } from './grid-builder.service';
import { AppService } from '../../../app.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { environment } from '../../../../environments/environment';
import { BatchActionEditComponent } from './batch-action-edit.component';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
interface GridHeader {
  call_back: string;
  col_class: string;
  data_type: string;
  display: string;
  drilldown: string;
  edit_type: string;
  editable: string;
  new_row_editable: string;
  format: string | null;
  full_label: string | null;
  grid_header_key: string;
  grid_key: string;
  header_class: string | null;
  label: string;
  map: string;
  validation: string | null;
  width: number;
  apply_rules: string;
  edit_rules: string;
  edit_rule_message: string;
  edit_icon: string;
  audit_cells: string;
  cell_class: string;
  drilldown_filter_refresh: string;
  drilldown_rule: string;
  documentation: number;
  pivot_unique_key: string | null;
  pivot_group_by_key: string | null;
  pivot_group_by_data: string | null;
  description: string;
  round_off: string;
  transpose: string;
  required: string;
  static_values?: string;
  isValid?: boolean;
  isValidate?: boolean;
  [key: string]: any;
}

interface GridModel {
  grid_key: number | null;
  action_key: number | null;
  grid_name: string;
  grid_desc: string;
  default_row_height: number;
  fixed_columns: number;
  header_height: number;
  headers: GridHeader[];
  grid_type: string;
  max_xls_rows: number;
  batch_select: number;
  expand_cols: number;
  comments: number;
  attachments: number;
  batch_select_rules: string | null;
  batch_rule_message: string | null;
  client_key: string | number;
  doc_header_key: string | null;
  doc_header_type: string | null;
  screen_validation: number;
  screen_validation_rules: any;
  rule_type: string | null;
  buffer_size: number;
  colMap: any[];
  colAmount: any[];
  audit_cells: number;
  documentation: number;
  [key: string]: any;
}

@Component({
    selector: 'gtw-grid-builder-edit',
    templateUrl: './grid-builder-edit.component.html',
    styleUrls: ['./grid-builder-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GridBuilderEditComponent implements OnInit, OnDestroy {

  loading = false;
  crudLoading = false;
  minimise = false;
  submitted = false;
  title = 'Grid Builder';
  isNew = true;
  showSave = false;
  actionOn = false;

  grid: GridModel = this.createEmptyGrid();
  apiKey = '';
  copyMessage = '';
  isValid = true;
  batchSelectRulesStr = '';

  actionNames: any[] = [];

  showConfirmRemoveAction = false;
  pendingRemoveActionKey: string | number | null = null;
  confirmRemoveActionConfig: ConfirmationModalConfig = {
    title: 'Remove Grid Action',
    message: 'Are you sure you want to remove this grid action?',
    iconClass: 'fa fa-exclamation-triangle',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };

  showConfirmDeleteHeader = false;
  pendingDeleteHeaderIdx: number | null = null;
  confirmDeleteHeaderConfig: ConfirmationModalConfig = {
    title: 'Delete Record',
    message: 'Are you sure you want to permanently delete this record?',
    iconClass: 'fa fa-exclamation-triangle',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };

  arrangeOpen = false;
  screenValidationOpen = false;
  rearrangeItems: (GridHeader & { selected?: boolean })[] = [];
  extraHeaders: GridHeader[] = [];
  isArrangeApplied = false;
  arrangeDragIdx: number | null = null;
  screenValidationRules: any[] = [];
  screenRuleTypes = [
    { name: 'colNetZero', value: 'colNetZero' },
    { name: 'groupNetZero', value: 'groupNetZero' },
  ];

  gridTypeList = [
    { name: 'Standard', value: 'standard' },
    { name: 'Dynamic', value: 'dynamic' },
    { name: 'Batch Save', value: 'batch-save' },
    { name: 'Pivot', value: 'pivot' },
    { name: 'Import', value: 'import' },
    { name: 'Custom', value: 'custom' },
  ];

  dataTypeList = [
    { name: 'string', value: 'string' },
    { name: 'number', value: 'number' },
    { name: 'date', value: 'date' },
  ];

  editTypeList = [
    { name: 'form', value: 'form' },
    { name: 'text', value: 'text' },
    { name: 'dropdown', value: 'dropdown' },
  ];

  iconTypeList = [
    { name: 'Edit', value: 'edit' },
    { name: 'Info', value: 'info' },
    { name: 'Remove', value: 'remove' },
    { name: 'Question', value: 'question' },
    { name: 'Eye', value: 'eye' },
    { name: 'Folder', value: 'folder' },
    { name: 'Download', value: 'download' },
    { name: 'Download File', value: 'downloadFile' },
    { name: 'File', value: 'file' },
  ];

  formatTypeList = [
    { name: 'String', value: '' },
    { name: 'Number with Commas', value: 'numberWithCommas' },
    { name: 'Float with Commas', value: 'floatWithCommas' },
    { name: 'Date', value: 'date' },
    { name: 'Checkbox', value: 'checkbox' },
    { name: 'Locked', value: 'locked' },
    { name: 'Color Dot', value: 'color-dot' },
    { name: 'Color Error', value: 'color-error' },
  ];

  validationTypeList = [
    { name: 'None', value: '' },
    { name: 'Number', value: 'numeric' },
    { name: 'Date', value: 'date' },
    { name: 'Uppercase', value: 'uppercase' },
    { name: 'Lowercase', value: 'lowercase' },
  ];

  transposeTypeList = [
    { name: 'None', value: '' },
    { name: 'Uppercase', value: 'uppercase' },
    { name: 'Lowercase', value: 'lowercase' },
  ];

  groupByDataOptions: { name: string; value: string }[] = [];

  private readonly baseUrl = environment.base_url;
  private readonly destroy$ = new Subject<void>();
  private rowData: any = {};

  constructor(
    @Optional() public dialogRef: MatDialogRef<GridBuilderEditComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private gridBuilderService: GridBuilderService,
    private appService: AppService,
    private snackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private elRef: ElementRef,
    private dialog: MatDialog,
  ) {
    this.rowData = data?.rowData || {};
  }

  private get clientKey(): string | number {
    return this.appService.USER_SETTINGS?.user?.client?.client_key
      ?? this.appService.USER_SETTINGS?.user?.client_key
      ?? '';
  }

  ngOnInit(): void {
    this.grid.client_key = this.clientKey;

    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.initFromRowData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createEmptyGrid(): GridModel {
    return {
      grid_key: null,
      action_key: null,
      grid_name: '',
      grid_desc: '',
      default_row_height: 30,
      fixed_columns: 2,
      header_height: 30,
      headers: [],
      grid_type: 'standard',
      max_xls_rows: 1000,
      batch_select: 0,
      expand_cols: 0,
      comments: 0,
      attachments: 0,
      batch_select_rules: null,
      batch_rule_message: null,
      client_key: '',
      doc_header_key: null,
      doc_header_type: null,
      screen_validation: 0,
      screen_validation_rules: null,
      rule_type: null,
      buffer_size: 0,
      colMap: [],
      colAmount: [],
      audit_cells: 0,
      documentation: 0,
    };
  }

  private createEmptyHeader(): GridHeader {
    return {
      call_back: '',
      col_class: '',
      data_type: 'string',
      display: 'y',
      drilldown: '',
      edit_type: '',
      editable: 'n',
      new_row_editable: '0',
      format: null,
      full_label: null,
      grid_header_key: '',
      grid_key: '',
      header_class: null,
      label: '',
      map: '',
      validation: null,
      width: 90,
      apply_rules: '0',
      edit_rules: '',
      edit_rule_message: '',
      edit_icon: '',
      audit_cells: '0',
      cell_class: '0',
      drilldown_filter_refresh: '0',
      drilldown_rule: '0',
      documentation: 0,
      pivot_unique_key: null,
      pivot_group_by_key: null,
      pivot_group_by_data: null,
      description: '',
      round_off: '0',
      transpose: '',
      required: '0',
    };
  }

  private initFromRowData(): void {
    const lower: any = {};
    Object.keys(this.rowData).forEach(k => {
      lower[k.toLowerCase()] = this.rowData[k];
    });

    this.grid = { ...this.createEmptyGrid(), ...lower };
    this.isNew = false;
    this.actionOn = false;
    this.showSave = true;

    this.loadHeaders(this.grid.grid_key!);
    this.generateApiKey();
  }

  private loadHeaders(gridKey: number | string): void {
    this.loading = true;
    this.cdr.detectChanges();

    this.gridBuilderService.getGridHeaderByKey({ grid_key: gridKey })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          if (res?.callSuccess === '1' && res?.jsonObject) {
            this.grid.headers = res.jsonObject;
            this.rebuildGroupByDataOptions();
            this.cloneGridHeaders();
            this.loadActionNames();
            this.parseScreenValidationRules();
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.snackbar.openSnackBar('Error loading grid headers', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private rebuildGroupByDataOptions(): void {
    this.groupByDataOptions = this.grid.headers
      .filter((_, i) => i > 0)
      .map(h => ({ name: h.map, value: h.map }));
  }

  loadActionNames(): void {
    if (!this.grid.grid_key) return;

    this.gridBuilderService.getActionsByGrid({
      client_key: this.grid.client_key,
      grid_key: this.grid.grid_key,
    }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.callSuccess === '1') {
            this.actionNames = res.gridActionData || [];
          } else {
            this.actionNames = res?.gridActionData || [];
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.snackbar.openSnackBar('Error loading action names', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  confirmRemoveAction(gridActionKey: string | number): void {
    this.pendingRemoveActionKey = gridActionKey;
    this.showConfirmRemoveAction = true;
    this.cdr.detectChanges();
  }

  onConfirmRemoveYes(): void {
    this.showConfirmRemoveAction = false;
    if (this.pendingRemoveActionKey != null) {
      this.removeActionName(this.pendingRemoveActionKey);
    }
    this.pendingRemoveActionKey = null;
    this.cdr.detectChanges();
  }

  onConfirmRemoveNo(): void {
    this.showConfirmRemoveAction = false;
    this.pendingRemoveActionKey = null;
    this.cdr.detectChanges();
  }

  removeActionName(gridActionKey: string | number): void {
    this.gridBuilderService.removeGridAction({
      client_key: this.grid.client_key,
      grid_action_key: gridActionKey,
    }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const success = res?.result?.value === '1' || res?.callSuccess === '1';
          if (success) {
            this.snackbar.openSnackBar('Grid Action successfully deleted', 'gtw-snackbar--green');
            this.loadActionNames();
          } else {
            this.snackbar.openSnackBar('Error occurred deleting grid action. If this continues please contact support', 'gtw-snackbar--red');
          }
        },
        error: () => {
          this.snackbar.openSnackBar('Error deleting grid action', 'gtw-snackbar--red');
        },
      });
  }

  generateApiKey(): void {
    this.apiKey = '';
    if (this.grid.grid_key && this.grid.action_key) {
      if (this.grid.grid_type === 'dynamic') {
        this.apiKey = `{"get":"${this.baseUrl}/loadDynamicGridJson?action_id=${this.grid.action_key}&grid_id=${this.grid.grid_key}"}`;
      } else if (this.grid.grid_type !== 'pivot') {
        this.apiKey = `{"get":"${this.baseUrl}/loadGridJson?action_id=${this.grid.action_key}&grid_id=${this.grid.grid_key}"}`;
      } else {
        this.apiKey = `{"get":"${this.baseUrl}/loadPivotGridJson?action_id=${this.grid.action_key}&grid_id=${this.grid.grid_key}"}`;
      }
    }
  }

  copyClipboard(): void {
    if (!this.apiKey) return;
    navigator.clipboard.writeText(this.apiKey).then(() => {
      this.copyMessage = 'API key was successfully copied to your clipboard';
      this.cdr.detectChanges();
      setTimeout(() => {
        this.copyMessage = '';
        this.cdr.detectChanges();
      }, 3000);
    });
  }

  // --- Header CRUD ---

  addHeader(): void {
    this.grid.headers.splice(1, 0, { ...this.createEmptyHeader() });
    this.cdr.detectChanges();
  }

  confirmDeleteHeader(index: number): void {
    this.pendingDeleteHeaderIdx = index;
    this.showConfirmDeleteHeader = true;
    this.cdr.detectChanges();
  }

  onConfirmDeleteHeaderYes(): void {
    this.showConfirmDeleteHeader = false;
    if (this.pendingDeleteHeaderIdx != null) {
      this.deleteHeader(this.pendingDeleteHeaderIdx);
    }
    this.pendingDeleteHeaderIdx = null;
    this.cdr.detectChanges();
  }

  onConfirmDeleteHeaderNo(): void {
    this.showConfirmDeleteHeader = false;
    this.pendingDeleteHeaderIdx = null;
    this.cdr.detectChanges();
  }

  deleteHeader(index: number): void {
    this.grid.headers.splice(index, 1);
    this.snackbar.openSnackBar('Header removed. Click Save to persist changes.', 'gtw-snackbar--green');
    this.cdr.detectChanges();
  }

  resetHeaders(): void {
    if (this.isNew) {
      this.grid.headers = [];
      this.genHeaders();
    } else {
      this.grid.headers = [];
      this.loadHeaders(this.grid.grid_key!);
    }
  }

  private isMoving = false;

  moveUp(index: number): void {
    if (index < 1 || this.isMoving) return;
    this.animateSwap(index, index - 1);
  }

  moveDown(index: number): void {
    if (index >= this.grid.headers.length - 1 || this.isMoving) return;
    this.animateSwap(index, index + 1);
  }

  private animateSwap(fromIdx: number, toIdx: number): void {
    this.isMoving = true;
    const hostEl = this.elRef.nativeElement as HTMLElement;
    const elA = hostEl.querySelector(`[data-header-idx="${fromIdx}"]`) as HTMLElement;
    const elB = hostEl.querySelector(`[data-header-idx="${toIdx}"]`) as HTMLElement;
    if (!elA || !elB) {
      [this.grid.headers[fromIdx], this.grid.headers[toIdx]] =
        [this.grid.headers[toIdx], this.grid.headers[fromIdx]];
      this.isMoving = false;
      this.cdr.detectChanges();
      return;
    }
    const rectA = elA.getBoundingClientRect();
    const rectB = elB.getBoundingClientRect();
    const deltaA = rectB.top - rectA.top;
    const deltaB = rectA.top - rectB.top;

    elA.style.transition = 'transform 0.3s ease';
    elB.style.transition = 'transform 0.3s ease';
    elA.style.transform = `translateY(${deltaA}px)`;
    elB.style.transform = `translateY(${deltaB}px)`;
    elA.style.zIndex = '2';
    elB.style.zIndex = '1';

    let done = false;
    const cleanup = () => {
      if (done) return;
      done = true;

      elA.style.transition = '';
      elA.style.transform = '';
      elA.style.zIndex = '';
      elB.style.transition = '';
      elB.style.transform = '';
      elB.style.zIndex = '';

      [this.grid.headers[fromIdx], this.grid.headers[toIdx]] =
        [this.grid.headers[toIdx], this.grid.headers[fromIdx]];
      this.isMoving = false;
      this.cdr.detectChanges();
    };

    elA.addEventListener('transitionend', cleanup, { once: true });
    setTimeout(cleanup, 400);
  }

  genHeaders(): void {
    if (!this.grid.action_key) {
      this.snackbar.openSnackBar('Action Key is required to generate headers', 'gtw-snackbar--yellow');
      return;
    }

    this.snackbar.openSnackBar('Generating headers...', 'gtw-snackbar--green');
    this.grid.headers = [];
    this.showSave = true;

    this.gridBuilderService.genGridHeader({ action_id: this.grid.action_key })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.callSuccess === '1' && res?.headerLabelMapList) {
            this.grid.headers = res.headerLabelMapList;
            this.rebuildGroupByDataOptions();
            this.cloneGridHeaders();
          } else {
            this.snackbar.openSnackBar(
              'Headers could not be generated. The action type must be set to \'grid\' and parameters must have correct default values.',
              'gtw-snackbar--red',
            );
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.snackbar.openSnackBar('Error generating headers', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  // --- Data type / transpose toggle ---

  dataTypeToggle(index: number, type: string): void {
    if (type !== 'number') {
      this.grid.headers[index].round_off = '0';
    }
  }

  transposeToggle(index: number, type: string): void {
    this.grid.headers[index].validation = type;
  }

  // --- JSON validation ---

  updateJsonObject(header?: GridHeader): void {
    if (header) {
      header.isValid = true;
      if (header.edit_rules && header.edit_rules !== '') {
        try {
          header.edit_rules = JSON.stringify(safeJsonInput<any>(header.edit_rules, null, 'grid-builder-edit.parse1'));
        } catch {
          header.isValid = false;
        }
      }
      header.isValidate = true;
      if (header.drilldown && header.drilldown !== '') {
        try {
          header.drilldown = JSON.stringify(safeJsonInput<any>(header.drilldown, null, 'grid-builder-edit.parse2'));
        } catch {
          header.isValidate = false;
        }
      }
    } else {
      this.isValid = true;
      if (this.grid.batch_select_rules) {
        try {
          this.batchSelectRulesStr = JSON.stringify(safeJsonInput<any>(this.grid.batch_select_rules as string, null, 'grid-builder-edit.parse3'));
        } catch {
          this.isValid = false;
        }
      }
    }
  }

  isHeaderHidden(header: GridHeader): boolean {
    return header.map === 'object_id' || header.map === 'OBJECT_ID';
  }

  isFirstVisible(index: number): boolean {
    for (let j = 0; j < index; j++) {
      if (!this.isHeaderHidden(this.grid.headers[j])) return false;
    }
    return true;
  }

  isLastVisible(index: number): boolean {
    for (let j = index + 1; j < this.grid.headers.length; j++) {
      if (!this.isHeaderHidden(this.grid.headers[j])) return false;
    }
    return true;
  }

  // --- Screen Validation Rules ---

  private parseScreenValidationRules(): void {
    if (this.grid.screen_validation_rules) {
      try {
        const parsed = typeof this.grid.screen_validation_rules === 'string'
          ? safeJsonInput<any>(this.grid.screen_validation_rules, null, 'grid-builder-edit.parse4')
          : this.grid.screen_validation_rules;
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.screenValidationRules = parsed;
        }
      } catch { /* ignore parse errors */ }
    }
  }

  addScreenValidationRule(): void {
    this.screenValidationRules.push({
      id: this.screenValidationRules.length + 1,
      rule_type: '',
      colMap: [],
      colAmount: [],
      buffer_size: 0,
    });
    this.cdr.detectChanges();
  }

  removeScreenValidationRule(index: number): void {
    this.screenValidationRules.splice(index, 1);
    this.cdr.detectChanges();
  }

  // --- Documentation Map (auto-add doc headers + batch actions when Documentation + Batch Select toggled) ---

  documentMap(): void {
    if (this.grid.batch_select == 1 && this.grid.documentation == 1) {
      if (this.grid.headers?.length > 0) {
        const docMap = this.grid.headers.filter(
          h => h.map === 'edit_documentation' || h.map === 'PARENT_KEY' || h.map === 'docu_comments' || h.map === 'PARENT_TYPE'
        );
        if (docMap.length === 0) {
          this.grid.headers.push(
            {
              ...this.createEmptyHeader(),
              call_back: 'editAttachment', label: 'Document', map: 'edit_documentation',
              data_type: 'string', display: 'y', edit_type: 'form', editable: 'y',
              edit_icon: 'file', width: 120, apply_rules: '1',
              edit_rules: '[{"map":"DOC_BATCH_ID","value":null,"operator":"!="}]',
              col_class: 'icon-center',
            },
            {
              ...this.createEmptyHeader(),
              call_back: '', label: 'Documents Batch ID', map: 'DOC_BATCH_ID',
              data_type: 'string', display: 'y', edit_type: 'text', editable: 'n',
              width: 120, apply_rules: '1',
              edit_rules: '[{"map":"DOC_BATCH_ID","value":null,"operator":"!="}]',
              col_class: 'center-align',
            },
          );
        }

        if (this.grid.grid_key != null && this.grid.grid_key !== undefined) {
          this.autoCreateDocBatchActions();
        }
      }
    } else {
      this.autoRemoveDocBatchActions();

      this.grid.headers = this.grid.headers.filter(
        h => h.map !== 'edit_documentation' && h.map !== 'HASDOCUMENTS'
             && h.map !== 'doc_batch_id' && h.map !== 'DOC_BATCH_ID'
      );

      if (!this.grid.documentation || this.grid.documentation === 0) {
        this.grid.doc_header_key = null;
        this.grid.doc_header_type = null;
      }
    }
    this.cdr.detectChanges();
  }

  private autoCreateDocBatchActions(): void {
    const docBatchActions = [
      {
        grid_action_key: '',
        name: 'Add Document',
        icon: 'fa fa-file',
        row_data_required: 1,
        call_back: 'addAttachment',
        grid_key: this.grid.grid_key,
        client_key: this.grid.client_key,
      },
      {
        grid_action_key: '',
        name: 'Remove Document',
        icon: 'fa fa-trash',
        row_data_required: 1,
        call_back: 'removeAttachment',
        grid_key: this.grid.grid_key,
        client_key: this.grid.client_key,
      },
    ];

    if (this.actionNames?.length > 0) {
      const actionsToCreate = docBatchActions.filter(action =>
        !this.actionNames.some(existing => existing.call_back === action.call_back)
      );
      actionsToCreate.forEach(action => {
        this.gridBuilderService.saveGridAction(action)
          .pipe(takeUntil(this.destroy$))
          .subscribe();
      });
      if (actionsToCreate.length > 0) {
        this.loadActionNames();
      }
    } else {
      docBatchActions.forEach(action => {
        this.gridBuilderService.saveGridAction(action)
          .pipe(takeUntil(this.destroy$))
          .subscribe();
      });
      this.loadActionNames();
    }
  }

  private autoRemoveDocBatchActions(): void {
    const docCallBacks = ['removeAttachment', 'addAttachment'];
    const toRemove = this.actionNames.filter(a => docCallBacks.includes(a.call_back));

    toRemove.forEach(action => {
      this.removeActionName(action.grid_action_key);
    });

    this.actionNames = this.actionNames.filter(a => !docCallBacks.includes(a.call_back));
  }

  // --- Arrange Grid Headers ---

  cloneGridHeaders(): void {
    this.rearrangeItems = this.grid.headers
      .filter(obj => obj.map !== 'object_id' && obj.map !== 'OBJECT_ID')
      .map(obj => ({ ...obj, selected: false }));
    this.extraHeaders = this.grid.headers
      .filter(obj => obj.map === 'object_id' || obj.map === 'OBJECT_ID');
    this.isArrangeApplied = false;
  }

  toggleArrangeItem(item: any): void {
    item.selected = !item.selected;
  }

  onArrangeDragStart(event: DragEvent, index: number): void {
    this.arrangeDragIdx = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    }
  }

  onArrangeDragEnter(event: DragEvent, targetIndex: number): void {
    event.preventDefault();
    if (this.arrangeDragIdx === null || this.arrangeDragIdx === targetIndex) return;
    const item = this.rearrangeItems.splice(this.arrangeDragIdx, 1)[0];
    this.rearrangeItems.splice(targetIndex, 0, item);
    this.arrangeDragIdx = targetIndex;
    this.isArrangeApplied = false;
    this.cdr.detectChanges();
  }

  onArrangeDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onArrangeDrop(event: DragEvent): void {
    event.preventDefault();
  }

  onArrangeDragEnd(): void {
    this.arrangeDragIdx = null;
    this.cdr.detectChanges();
  }

  moveArrangeUp(): void {
    for (let i = 1; i < this.rearrangeItems.length; i++) {
      if (this.rearrangeItems[i].selected && !this.rearrangeItems[i - 1].selected) {
        [this.rearrangeItems[i - 1], this.rearrangeItems[i]] =
          [this.rearrangeItems[i], this.rearrangeItems[i - 1]];
      }
    }
    this.isArrangeApplied = false;
    this.cdr.detectChanges();
  }

  moveArrangeDown(): void {
    for (let i = this.rearrangeItems.length - 2; i >= 0; i--) {
      if (this.rearrangeItems[i].selected && !this.rearrangeItems[i + 1].selected) {
        [this.rearrangeItems[i], this.rearrangeItems[i + 1]] =
          [this.rearrangeItems[i + 1], this.rearrangeItems[i]];
      }
    }
    this.isArrangeApplied = false;
    this.cdr.detectChanges();
  }

  applyArrangeOrder(): void {
    const cleaned = this.rearrangeItems.map(item => {
      const { selected, ...header } = item;
      return header as GridHeader;
    });
    this.grid.headers = [...this.extraHeaders, ...cleaned];
    this.isArrangeApplied = true;
    this.rearrangeItems.forEach(item => item.selected = false);
    this.rebuildGroupByDataOptions();
    this.snackbar.openSnackBar(
      'New header order applied. Click Save to persist changes.',
      'gtw-snackbar--green',
    );
    this.cdr.detectChanges();
  }

  resetArrangeOrder(): void {
    if (this.isArrangeApplied) {
      this.snackbar.openSnackBar(
        'Applied changes can\'t be reverted. Please close the modal without saving and try again.',
        'gtw-snackbar--yellow',
      );
      return;
    }
    this.cloneGridHeaders();
    this.cdr.detectChanges();
  }

  // --- Batch Action (sub-modal) ---

  editGridAction(actionData: any): void {
    this.openBatchActionModal(actionData);
  }

  addBatchAction(): void {
    this.openBatchActionModal(null);
  }

  private openBatchActionModal(actionData: any): void {
    const config = new MatDialogConfig();
    config.panelClass = ['admin-modal-panel', 'admin-child-modal-panel'];
    config.width = '850px';
    config.maxWidth = '90vw';
    config.disableClose = true;
    config.data = {
      grid_key: this.grid.grid_key,
      client_key: this.clientKey,
      actionData,
    };

    const ref = this.dialog.open(BatchActionEditComponent, config);
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result === 'saved') {
        this.loadActionNames();
      }
    });
  }

  // --- Save ---

  saveGrid(): void {
    if (this.crudLoading) {
      this.snackbar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    this.submitted = true;
    this.cdr.detectChanges();

    if (!this.grid.grid_name?.trim()) {
      this.snackbar.openSnackBar('Grid Name is required', 'gtw-snackbar--yellow');
      return;
    }
    if (!this.grid.grid_desc?.trim()) {
      this.snackbar.openSnackBar('Grid Description is required', 'gtw-snackbar--yellow');
      return;
    }

    if (this.grid.headers) {
      const hasAuditRecords = this.grid.headers.filter(h => h.audit_cells === '1');
      if (hasAuditRecords.length > 0 && this.grid.audit_cells === 0) {
        this.grid.audit_cells = 1;
      }
    }

    if (!this.grid.documentation || this.grid.documentation === 0) {
      this.grid.doc_header_key = null;
      this.grid.doc_header_type = null;
    }

    if (!this.grid.screen_validation || this.grid.screen_validation === 0) {
      this.screenValidationRules = [];
      this.grid.screen_validation_rules = [];
    } else {
      this.grid.screen_validation_rules = JSON.stringify(this.screenValidationRules);
    }

    const uniqueHeaders = this.deduplicateHeaders(this.grid.headers);

    const payload = {
      grid_key: this.grid.grid_key,
      action_key: this.grid.action_key,
      grid_name: this.grid.grid_name,
      grid_desc: this.grid.grid_desc,
      default_row_height: this.grid.default_row_height,
      batch_select: this.grid.batch_select,
      comments: this.grid.comments,
      grid_type: this.grid.grid_type,
      is_pivot: this.grid.is_pivot,
      max_xls_rows: this.grid.max_xls_rows,
      expand_cols: this.grid.expand_cols,
      fixed_columns: this.grid.fixed_columns,
      client_key: this.grid.client_key,
      attachments: this.grid.attachments,
      header_data: JSON.stringify(uniqueHeaders),
      batch_select_rules: this.grid.batch_select_rules,
      batch_rule_message: this.grid.batch_rule_message,
      header_height: this.grid.header_height,
      documentation: this.grid.documentation,
      doc_header_key: this.grid.doc_header_key,
      doc_header_type: this.grid.doc_header_type,
      screen_validation: this.grid.screen_validation,
      screen_validation_rules: typeof this.grid.screen_validation_rules === 'string'
        ? this.grid.screen_validation_rules
        : JSON.stringify(this.grid.screen_validation_rules || []),
      audit_cells: this.grid.audit_cells,
    };

    this.crudLoading = true;
    this.cdr.detectChanges();

    this.gridBuilderService.saveGrid(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.crudLoading = false;
          if (res?.callSuccess === '1') {
            this.snackbar.openSnackBar('Grid has been successfully saved', 'gtw-snackbar--green');
            if (res.grid_key_out && this.isNew) {
              this.isNew = false;
              this.grid.grid_key = res.grid_key_out;
              this.actionOn = false;
              this.showSave = true;
              this.generateApiKey();
              this.cdr.detectChanges();
            }
            this.dialogRef?.close('saved');
          } else {
            const error = res?.errorMessage || 'An error has occurred saving the grid.';
            this.snackbar.openSnackBar(error, 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.crudLoading = false;
          this.snackbar.openSnackBar('Error saving grid', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private deduplicateHeaders(headers: GridHeader[]): GridHeader[] {
    const seen = new Set<string>();
    return headers.filter(h => {
      const key = `${h.map}|${h.label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  cancel(): void {
    this.dialogRef?.close();
  }
}
